import { Dispatch, SetStateAction, useMemo } from "react";
import { Box } from "@chakra-ui/react";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  DocumentType,
  FragmentType,
  getFragmentData,
  gql,
} from "__generated__/gql";
import ProductAttributesMatrixAttributeAssignmentCheckbox from "components/Products/ProductAttributesMatrix/AttributeAssignmentCheckbox";
import { makeCheckboxSelectionCellWithAll } from "components/Shared/ChakraReactTable/CheckboxSelectionCell";
import FullPageTable, {
  useFullPageTableProps,
} from "components/Shared/ChakraReactTable/FullPageTable";
import SkuNameCell from "components/Shared/SkuNameCell";
import useFixedLayout from "components/Shared/ChakraReactTable/useFixedLayout";
import { usePrepApolloData } from "components/Shared/ChakraReactTable/utils";
import {
  SelectableRowTableMeta,
  useTableCheckboxSelection,
} from "components/Shared/ChakraReactTable/useSelectableRow";
import useStaticColumnPinning from "components/Shared/ChakraReactTable/useStaticColumnPinning";
import { BooleanCell } from "components/Shared/ChakraReactTable/formatters";
import AttributeIsCertificationIcon from "components/Shared/AttributeIsCertificationIcon";

const attributeFragment = gql(/* GraphQL */ `
  fragment ProductAttributesMatrixTableAttribute on Attribute {
    id
    name
    description
    isCommon
    isCertification
  }
`);

const skuFragment = gql(/* GraphQL */ `
  fragment ProductAttributesMatrixTableSku on Sku {
    id
    ...SkuNameCell
  }
`);

type Datum = DocumentType<typeof skuFragment>;
type Meta = SelectableRowTableMeta<Datum> & {
  isAllSelected: boolean;
  isIndeterminate: boolean;
  isSelectAllDisabled: boolean;
  onIsAllSelectedChange: (isAllSelected: boolean) => void;
};

const col = createColumnHelper<Datum>();

type Props = {
  attributes: readonly FragmentType<typeof attributeFragment>[];
  isEditMode: boolean;
  isLoading?: boolean;
  onRefresh?: () => Promise<unknown>;
  onSelectedSkuIdsChange: Dispatch<SetStateAction<UUID[]>>;
  selectedSkuIds: UUID[];
  skuAttributeIdsBySkuId: Readonly<Record<string, readonly UUID[]>>;
  skus: readonly FragmentType<typeof skuFragment>[];
};

export default function ProductAttributesMatrixTable({
  attributes,
  isEditMode,
  isLoading,
  onRefresh,
  onSelectedSkuIdsChange,
  selectedSkuIds,
  skuAttributeIdsBySkuId,
  skus,
}: Props) {
  const preparedAttributes = usePrepApolloData(
    getFragmentData(attributeFragment, attributes),
  );
  const preparedSkus = usePrepApolloData(getFragmentData(skuFragment, skus));
  const rowIds = useMemo(
    () => preparedSkus.map((sku) => sku.id),
    [preparedSkus],
  );
  const selectedSkuIdSet = useMemo(
    () => new Set(selectedSkuIds),
    [selectedSkuIds],
  );

  const isAllSelected =
    rowIds.length > 0 &&
    rowIds.reduce(
      (count, rowId) => count + Number(selectedSkuIdSet.has(rowId)),
      0,
    ) === rowIds.length;
  const isSomeSelected =
    !isAllSelected && rowIds.some((rowId) => selectedSkuIdSet.has(rowId));
  const isSelectAllDisabled = rowIds.length === 0;

  const { selectableRowTableMeta, selectableRowTableProps } =
    useTableCheckboxSelection<Datum>({
      isMulti: true,
      isAllSelected,
      selected: selectedSkuIds,
      onSelectedChange: onSelectedSkuIdsChange,
      getIdForRow: (row) => row.original.id,
    });

  const meta = useMemo<Meta>(
    () => ({
      ...selectableRowTableMeta,
      isAllSelected,
      isIndeterminate: isSomeSelected,
      isSelectAllDisabled,
      onIsAllSelectedChange: (newIsAllSelected) => {
        onSelectedSkuIdsChange((existing) => {
          if (newIsAllSelected) {
            const newSelected = new Set(existing);
            rowIds.forEach((id) => newSelected.add(id));
            return Array.from(newSelected);
          }

          return existing.filter((id) => !rowIds.includes(id));
        });
      },
    }),
    [
      selectableRowTableMeta,
      isAllSelected,
      isSomeSelected,
      isSelectAllDisabled,
      onSelectedSkuIdsChange,
      rowIds,
    ],
  );

  const columns = useMemo(() => {
    const skuColumn = col.display({
      id: "product",
      header: "Product",
      cell: (cell) => (
        <Box display="flex" flexFlow="column" alignItems="flex-start">
          <SkuNameCell
            sku={cell.row.original}
            popoverProps={{ isInPortal: true }}
          />
        </Box>
      ),
      size: 220,
      meta: { pinned: "left" },
    });

    const attributeColumns = preparedAttributes.map((attribute) =>
      col.display({
        id: `attribute-${attribute.id}`,
        header: () => (
          <>
            {attribute.name}
            {attribute.isCertification ? (
              <AttributeIsCertificationIcon />
            ) : null}
          </>
        ),
        cell: (cell) => {
          const skuId = cell.row.original.id;
          const skuAttributeIdSet = new Set(
            skuAttributeIdsBySkuId[skuId] || [],
          );
          const isAssigned = skuAttributeIdSet.has(attribute.id);

          if (isEditMode) {
            return (
              <ProductAttributesMatrixAttributeAssignmentCheckbox
                skuId={skuId}
                attributeId={attribute.id}
                isAssigned={isAssigned}
              />
            );
          }

          return (
            <BooleanCell
              value={isAssigned}
              trueProps={{ color: "green.600" }}
              falseProps={{ opacity: 0.1 }}
            />
          );
        },
        size: 120,
        meta: { props: { textAlign: "center" } },
      }),
    );

    if (isEditMode) {
      const selectionColumn = makeCheckboxSelectionCellWithAll<Datum, Meta>(
        col,
        { meta: { pinned: "left" } },
      );
      return [selectionColumn, skuColumn, ...attributeColumns];
    }

    return [skuColumn, ...attributeColumns];
  }, [isEditMode, preparedAttributes, skuAttributeIdsBySkuId]);
  const columnPinning = useStaticColumnPinning(columns);

  const tableInstance = useReactTable({
    meta,
    columns,
    data: preparedSkus,
    getCoreRowModel: getCoreRowModel(),
    state: { columnPinning },
  });

  const tableProps = useFullPageTableProps<Datum>([
    useFixedLayout(tableInstance),
    isEditMode ? selectableRowTableProps : {},
    { tableProps: { size: "sm" } },
  ]);

  return (
    <Box display="flex" flexDirection="column" flex="1 1 100%" minH={0}>
      <FullPageTable
        tableInstance={tableInstance}
        {...tableProps}
        isLoading={isLoading}
        isPaginationEnabled={false}
        noRowsPlaceholder={isLoading ? "Loading SKUs…" : "No SKUs found."}
        totalCount={preparedSkus.length}
        resultTypeLabel="SKUs"
        onRefresh={onRefresh}
      />
    </Box>
  );
}
